import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Row, Col, Card, Spinner, Badge, Button } from "react-bootstrap";
import { FaArrowLeft } from "react-icons/fa";
import APIServicenew from "../../utils/APIGeneralService";
import { UserContext } from "../../context/UserContextProvider";
import { formateDatemdy } from "../../utils/Helper";

const ActionLogDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const authToken = process.env.REACT_APP_authToken;
  const { accessToken, logout } = useContext(UserContext);

  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = {
    "auth-token": authToken,
    "authorization": accessToken
  };

  const APIServices = new APIServicenew(process.env.REACT_APP_OTHER_API_URL);

  useEffect(() => {
    if (id) {
      fetchLogDetails();
    }
  }, [id]);

  const fetchLogDetails = async () => {
    setLoading(true);
    try {
      const response = await APIServices.get(
        `/admin/cms/action_logs/${id}`,
        headers
      );

      if (response?.data?.status === true) {
        setLog(response?.data?.data);
      } else if (response?.status === 401) {
        logout();
        navigate("/");
      }
    } catch (error) {
      console.error("Error fetching log details:", error);
    } finally {
      setLoading(false);
    }
  };

  const getActionBadge = (action) => {
    switch (action) {
      case 'CREATE':
        return <Badge bg="success">{action}</Badge>;
      case 'UPDATE':
        return <Badge bg="warning" className="text-dark">{action}</Badge>;
      case 'DELETE':
        return <Badge bg="danger">{action}</Badge>;
      case 'DUPLICATE':
        return <Badge bg="info">{action}</Badge>;
      default:
        return <Badge bg="secondary">{action}</Badge>;
    }
  };

  const compareData = (oldData, newData) => {
    const allKeys = new Set([...Object.keys(oldData || {}), ...Object.keys(newData || {})]);
    const changes = [];

    allKeys.forEach(key => {
      const oldValue = oldData?.[key];
      const newValue = newData?.[key];
      const isArrayField = Array.isArray(oldValue) || Array.isArray(newValue);
      const isChanged = JSON.stringify(oldValue) !== JSON.stringify(newValue);
      
      changes.push({
        key,
        oldValue,
        newValue,
        isChanged,
        isArray: isArrayField,
        isNewOnly: oldValue === undefined && newValue !== undefined,
        isDeleted: oldValue !== undefined && newValue === undefined
      });
    });

    return changes.sort((a, b) => {
      if (a.isChanged && !b.isChanged) return -1;
      if (!a.isChanged && b.isChanged) return 1;
      if (a.isNewOnly && !b.isNewOnly) return -1;
      if (!a.isNewOnly && b.isNewOnly) return 1;
      return 0;
    });
  };

  const isArrayData = (value) => {
    return Array.isArray(value) && value.length > 0 && value.every(item => typeof item === 'object');
  };

  const isMediaField = (key) => {
    const mediaFields = ['image', 'logo', 'profile_pic', 'profile_pic', 'video', 'document', 'file', 'pdf', 'icon', 'thumbnail', 'banner', 'photo', 'pic', 'media'];
    const lowerKey = key.toLowerCase();
    return mediaFields.some(field => lowerKey.includes(field));
  };

  const isVideoUrl = (url) => {
    if (typeof url !== 'string') return false;
    return url.match(/\.(mp4|webm|ogg|mov|avi)(\?|$)/i) || url.includes('video') || url.includes('mp4');
  };

  const isImageUrl = (url) => {
    if (typeof url !== 'string') return false;
    return url.match(/\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i);
  };

  const isDocumentUrl = (url) => {
    if (typeof url !== 'string') return false;
    return url.match(/\.(pdf|doc|docx|xls|xlsx|ppt|pptx)(\?|$)/i);
  };

  const renderMediaPreview = (url, fieldName) => {
    if (!url || typeof url !== 'string') return null;

    if (isImageUrl(url)) {
      return (
        <div className="mt-1">
          <img 
            src={url} 
            alt={fieldName}
            style={{ maxWidth: '150px', maxHeight: '120px', objectFit: 'cover', borderRadius: '4px' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      );
    }

    if (isVideoUrl(url)) {
      return (
        <div className="mt-1">
          <video 
            src={url} 
            controls 
            style={{ maxWidth: '200px', maxHeight: '120px' }}
          />
        </div>
      );
    }

    if (isDocumentUrl(url)) {
      return (
        <div className="mt-1">
          <a href={url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
            View Document
          </a>
        </div>
      );
    }

    return null;
  };

  const renderArrayData = (data, label) => {
    if (!Array.isArray(data) || data.length === 0) {
      return <span className="text-muted">—</span>;
    }

    return (
      <div className="mb-2">
        {data.map((item, index) => (
          <div key={index} className="border rounded p-2 mb-2 bg-light">
            <small className="text-muted fw-bold">{label} {index + 1}</small>
            <div className="row">
              {Object.entries(item).map(([key, value]) => (
                <div key={key} className="col-md-6 mb-1">
                  <strong className="small">{key}:</strong> <span className="small">{String(value)}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderFieldValue = (value, isChanged, type, fieldKey) => {
    if (value === undefined || value === null) {
      return '—';
    }

    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
      return renderArrayData(value, type === 'old' ? 'Old' : type === 'new' ? 'New' : '');
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    const stringValue = String(value);
    const fieldName = fieldKey || '';

    const mediaPreview = renderMediaPreview(stringValue, fieldName);
    if (mediaPreview) {
      return (
        <>
          <span className="text-muted">{stringValue}</span>
          {mediaPreview}
        </>
      );
    }

    return stringValue;
  };

  const renderChangeLog = () => {
    if (log.action === 'CREATE') {
      return (
        <Card>
          <Card.Body>
            <h6 className="text-primary mb-3">New Data</h6>
            <table className="table table-bordered table-sm">
              <thead className="table-light">
                <tr>
                  <th>Field</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(log.new_data || {}).map(([key, value]) => (
                  <tr key={key} className="table-success">
                    <td className="fw-semibold">{key}</td>
                    <td className="text-success">
                      {renderFieldValue(value, false, 'new', key)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card.Body>
        </Card>
      );
    }

    if (log.action === 'DELETE') {
      return (
        <Card>
          <Card.Body>
            <h6 className="text-primary mb-3">Old Data (Deleted)</h6>
            <table className="table table-bordered table-sm">
              <thead className="table-light">
                <tr>
                  <th>Field</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(log.old_data || {}).map(([key, value]) => (
                  <tr key={key} className="table-danger">
                    <td className="fw-semibold">{key}</td>
                    <td className="text-danger">
                      {renderFieldValue(value, false, 'old', key)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card.Body>
        </Card>
      );
    }

    const changes = compareData(log.old_data, log.new_data);

    return (
      <>
        <Row>
          <Col md={6}>
            <Card>
              <Card.Body>
                <h6 className="text-primary mb-3">Old Data</h6>
                <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  <table className="table table-bordered table-sm mb-0">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th>Field</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {changes.map((change) => (
                        <tr 
                          key={change.key}
                          className={change.isChanged && !change.isNewOnly ? 'table-danger' : ''}
                        >
                          <td className="fw-semibold">{change.key}</td>
                          <td className={change.isChanged && !change.isNewOnly ? 'text-danger' : ''}>
                            {renderFieldValue(change.oldValue, change.isChanged, 'old', change.key)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card>
              <Card.Body>
                <h6 className="text-primary mb-3">New Data</h6>
                <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  <table className="table table-bordered table-sm mb-0">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th>Field</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {changes.map((change) => (
                        <tr 
                          key={change.key}
                          className={change.isChanged && !change.isDeleted ? 'table-warning' : ''}
                        >
                          <td className="fw-semibold">{change.key}</td>
                          <td className={change.isChanged && !change.isDeleted ? 'text-success' : ''}>
                            {renderFieldValue(change.newValue, change.isChanged, 'new', change.key)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading log details...</p>
      </div>
    );
  }

  if (!log) {
    return (
      <div className="text-center py-5">
        <p>No log found</p>
        <Button variant="primary" onClick={() => navigate("/admin/action-logs")}>
          Back to Action Logs
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="action_log_details_page">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="font_blue_18 font-600">
            Action Log Details
          </h1>

          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={() => navigate("/admin/action-logs")}>
              <FaArrowLeft className="me-1" /> Back
            </Button>
          </div>
        </div>

        <Row>
          <Col md={12}>
            <Card>
              <Card.Body>
                <h5 className="text-primary mb-4">Log Information</h5>
                
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <strong>Table Name:</strong> <span className="ms-2">{log.table_name}</span>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <strong>Record ID:</strong> <span className="ms-2">{log.record_id}</span>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <strong>Action:</strong> <span className="ms-2">{getActionBadge(log.action)}</span>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <strong>Date:</strong> <span className="ms-2">{formateDatemdy(log.created)}</span>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="mb-3">
                      <strong>Description:</strong>
                      <p className="mt-1">{log.description}</p>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <strong>Performed By:</strong> <span className="ms-2">{log.performer_name}</span>
                      <small className="text-muted ms-1">({log.perform_by})</small>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <strong>IP Address:</strong> <span className="ms-2 text-muted">{log.ip_address}</span>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={12}>
            {renderChangeLog()}
          </Col>
        </Row>
      </div>
    </>
  );
};

export default ActionLogDetails;